import { z } from 'zod';
import {
  ResolvedFormConfig,
  ResolvedFieldConfig,
  FormValueSchema,
  PresentationsDataEntityTypeSchema
} from '../types';

/**
 * Creates a Zod schema for an array field based on its configuration.
 * Handles minCount, maxCount, and required validation.
 */
const createArrayFieldSchema = (
  fieldConfig: ResolvedFieldConfig,
  fieldName: string
) => {
  // If field is hidden, make it optional with no validation
  if (fieldConfig.visibility === 'hidden') {
    return z.array(FormValueSchema).optional();
  }

  let schema = z.array(FormValueSchema);

  // Apply minCount validation
  // minCount >= 1 means the field is required
  if (fieldConfig.minCount !== undefined && fieldConfig.minCount > 0) {
    schema = schema.min(
      fieldConfig.minCount,
      `Select at least ${fieldConfig.minCount} ${fieldName}${fieldConfig.minCount > 1 ? 's' : ''}`
    );
  }

  // Apply maxCount validation
  if (fieldConfig.maxCount !== undefined) {
    schema = schema.max(
      fieldConfig.maxCount,
      `Select at most ${fieldConfig.maxCount} ${fieldName}${fieldConfig.maxCount > 1 ? 's' : ''}`
    );
  }

  return schema;
};

/**
 * Validates an array field against minCount and maxCount.
 * Returns true if valid, false if invalid.
 */
export const validateArrayFieldCount = (
  items: unknown[] | undefined,
  minCount: number | undefined,
  maxCount: number | undefined
): boolean => {
  const count = items?.length ?? 0;
  
  if (minCount !== undefined && count < minCount) {
    return false;
  }
  
  if (maxCount !== undefined && count > maxCount) {
    return false;
  }
  
  return true;
};

/**
 * Creates a Zod schema for a single-value field based on its configuration.
 * minCount >= 1 means the field is required.
 */
const createSingleFieldSchema = (
  fieldConfig: ResolvedFieldConfig,
  _fieldName: string
) => {
  // If field is hidden, make it optional
  if (fieldConfig.visibility === 'hidden') {
    return FormValueSchema.optional();
  }

  // minCount >= 1 means required
  const isRequired = (fieldConfig.minCount ?? 0) >= 1;
  if (!isRequired) {
    return FormValueSchema.optional();
  }

  return FormValueSchema;
};

/**
 * Creates a dynamic Zod schema for the Selections step based on form config.
 * This replaces the static SelectionsStepSchema when using dynamic config.
 */
export const createSelectionsSchema = (config: ResolvedFormConfig) => {
  const baseSchema = z.object({
    currency: z.string().nonempty('Currency is required'),
    retailerIds: createArrayFieldSchema(config.retailerIds, 'retailer'),
    entityType: createSingleFieldSchema(config.entityType, 'entity type'),
    entityId: createSingleFieldSchema(config.entityId, 'entity'),
    categoryIds: createArrayFieldSchema(config.categoryIds, 'category'),
    subcategoryIds: createArrayFieldSchema(config.subcategoryIds, 'subcategory'),
    // brandIds is ALWAYS optional on Selections step - validated on Brands step
    brandIds: z.array(FormValueSchema).optional(),
  });

  // Add conditional validation only if relevant fields are visible
  return baseSchema.superRefine((data, ctx) => {
    // Retailer validation (if visible and has constraints)
    if (config.retailerIds.visibility === 'visible') {
      const retailerCount = data.retailerIds?.length ?? 0;
      
      // Check exact count if both min and max are the same
      if (
        config.retailerIds.minCount !== undefined &&
        config.retailerIds.maxCount !== undefined &&
        config.retailerIds.minCount === config.retailerIds.maxCount &&
        retailerCount !== config.retailerIds.minCount
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `Please select exactly ${config.retailerIds.minCount} retailer${config.retailerIds.minCount > 1 ? 's' : ''}`,
          path: ['retailerIds'],
        });
      }
    }

    // Entity type / category / subcategory validation (only if entityType is visible)
    if (config.entityType.visibility === 'visible' && data.entityType) {
      const isCategory = data.entityType.id === PresentationsDataEntityTypeSchema.Enum.category;
      
      if (isCategory) {
        // If category entity type, need either categories or subcategories
        if (
          config.categoryIds.visibility === 'visible' ||
          config.subcategoryIds.visibility === 'visible'
        ) {
          const hasCategories = data.categoryIds && data.categoryIds.length > 0;
          const hasSubcategories = data.subcategoryIds && data.subcategoryIds.length > 0;

          if (!hasCategories && !hasSubcategories) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Either Categories or Subcategories must be selected',
              path: ['categoryIds'],
            });
          }
        }
      } else {
        // Segment or Business Unit - entityId is required
        if (config.entityId.visibility === 'visible' && (!data.entityId || !data.entityId.id)) {
          const isSegment = data.entityType.id === PresentationsDataEntityTypeSchema.Enum.segment;
          const prefix = isSegment ? 'Segment' : 'Business Unit';

          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `${prefix} cannot be empty`,
            path: ['entityId'],
          });
        }
      }
    }

    // NOTE: brandIds validation is NOT done here - it's ALWAYS validated on the Brands step
    // Brand selection UI is always on the Brands page, so we validate there
  });
};

/**
 * Checks if brand selection should be validated on the Brands step.
 * This happens when brandGroupings is hidden AND brandIds is required but NOT visible on Selection step.
 */
export const shouldValidateBrandIdsOnBrandsStep = (config: ResolvedFormConfig): boolean => {
  // Brand selection is on Brands step when:
  // - brandGroupings is hidden AND
  // - brandIds has minCount >= 1 (derived from minCount, not 'required')
  const brandSelectionRequired = (config.brandIds.minCount ?? 0) >= 1;
  return config.brandGroupings.visibility === 'hidden' && brandSelectionRequired;
};

/**
 * Creates a dynamic Zod schema for the Brands step based on form config.
 * This replaces the static BrandsStepSchema when using dynamic config.
 */
export const createBrandsSchema = (config: ResolvedFormConfig) => {
  return z.object({
    brandGroupings: config.brandGroupings.visibility === 'visible'
      ? FormValueSchema
      : FormValueSchema.optional(),
  });
};

/**
 * Type for the dynamic selections step (inferred from the schema factory).
 * Note: This is a superset that includes brandIds.
 * All fields except currency are optional since visibility can make them hidden.
 */
export type DynamicSelectionsStep = {
  currency: string;
  retailerIds?: Array<{ id: string; name: string }>;
  entityType?: { id: string; name: string };
  entityId?: { id: string; name: string };
  categoryIds?: Array<{ id: string; name: string }>;
  subcategoryIds?: Array<{ id: string; name: string }>;
  brandIds?: Array<{ id: string; name: string }>;
};

/**
 * Type for the dynamic brands step.
 */
export type DynamicBrandsStep = {
  brandGroupings?: { id: string; name: string };
};

